import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useRegisterSW } from "virtual:pwa-register/react";
import { sileo } from "sileo";
import { usePwaInstall } from "./usePwaInstall";

/**
 * Global PWA notifications, surfaced through sileo (the app's toast system,
 * mounted once at the root in AppProviders). Logic-only — renders nothing.
 *
 *  - update available → persistent actionable "Actualizar" toast
 *  - install affordance → "Instalar" toast (Chromium) / iOS A2HS hint
 *  - offline ready → transient confirmation
 */
const PwaManager = () => {
  const { t } = useTranslation();

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  const { canPrompt, isIos, isInstalled, promptInstall } = usePwaInstall();

  const updateIdRef = useRef<string | null>(null);
  const installIdRef = useRef<string | null>(null);
  const installShownRef = useRef(false);

  // New version waiting → actionable toast; reloading is the user's call so we
  // don't yank the page out from under an admin mid-task.
  useEffect(() => {
    if (!needRefresh) return;

    updateIdRef.current = sileo.action({
      title: t("PWA.update.title"),
      description: t("PWA.update.body"),
      duration: null,
      button: {
        title: t("PWA.update.action"),
        onClick: () => {
          setNeedRefresh(false);
          void updateServiceWorker(true);
        },
      },
    });

    return () => {
      if (updateIdRef.current) {
        sileo.dismiss(updateIdRef.current);
        updateIdRef.current = null;
      }
    };
  }, [needRefresh, setNeedRefresh, updateServiceWorker, t]);

  // Offline shell cached → one-off confirmation.
  useEffect(() => {
    if (!offlineReady) return;

    sileo.success({
      title: t("PWA.offlineReady.title"),
      description: t("PWA.offlineReady.body"),
      duration: 6000,
    });

    setOfflineReady(false);
  }, [offlineReady, setOfflineReady, t]);

  // Install affordance → once per session, until installed or acted on.
  useEffect(() => {
    if (isInstalled || installShownRef.current) return;
    if (!canPrompt && !isIos) return;

    installShownRef.current = true;

    if (isIos) {
      sileo.info({
        title: t("PWA.install.title"),
        description: t("PWA.install.iosHint"),
        duration: null,
      });
      return;
    }

    installIdRef.current = sileo.action({
      title: t("PWA.install.title"),
      description: t("PWA.install.body"),
      duration: null,
      button: {
        title: t("PWA.install.action"),
        onClick: () => {
          if (installIdRef.current) sileo.dismiss(installIdRef.current);
          void promptInstall();
        },
      },
    });
  }, [canPrompt, isIos, isInstalled, promptInstall, t]);

  return null;
};

export default PwaManager;
